/**
 * @fileoverview Plumbing Maintenance Checklist Component
 * @module PlumbingChecklist
 * 
 * Includes helper blade integration for detailed guidance on each checklist item.
 * Supports inline photo uploads for photo-worthy checklist items.
 */

import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import ChecklistQuestion, { EvidenceDocument } from './ChecklistQuestion';
import RepairCostSection from './RepairCostSection';
import HelperBlade from './HelperBlade';
import { PhotoUploader, EvidenceGallery } from '../evidence';
import { Droplets, ArrowLeft, Save, Camera } from 'lucide-react';

interface PlumbingChecklistProps {
  initialData?: Record<string, any>;
  onSave: (data: Record<string, any>) => Promise<void>;
  onBack: () => void;
  findingId?: string;
  evidence?: EvidenceDocument[];
  invoices?: any[];
  onEvidenceChange?: (evidence?: any) => void;
}

type FollowUpDataType = Record<string, Record<string, string | number>>;

const plumbingQuestions = [
  { id: 'pipe_age', question: 'Are the pipes in your home original or over 30 years old?', helpText: 'Galvanized or polybutylene pipes common in older Texas homes often need replacement.' },
  { id: 'low_water_pressure', question: 'Is there low water pressure throughout the home?', helpText: 'May indicate pipe corrosion, mineral buildup, or slab leaks.' },
  { id: 'slow_drains', question: 'Are there slow drains in multiple fixtures?', helpText: 'Could indicate main line issues or tree root intrusion.' },
  { id: 'water_discoloration', question: 'Is there discolored water from faucets?', helpText: 'Rust or sediment indicates galvanized pipe deterioration.' },
  { id: 'visible_leaks', question: 'Are there visible leaks under sinks or around fixtures?', helpText: 'Check cabinets and visible pipes for water damage.' },
  { id: 'water_heater_issues', question: 'Is the water heater over 8-10 years old or having issues?', helpText: 'Hard Texas water shortens water heater life; check for sediment and corrosion.' },
  { id: 'slab_leak', question: 'Is there evidence of a slab leak?', helpText: 'Hot spots on floor, unexplained water bills, foundation movement - very common in Texas.' },
  { id: 'sewer_issues', question: 'Are there sewer odors or backup issues?', helpText: 'Texas clay soil shifts and can damage sewer lines; tree roots also cause problems.' },
  { id: 'polybutylene_pipes', question: 'Does your home have polybutylene (gray) pipes?', helpText: 'Common in 1978-1995 Texas homes; prone to failure, often uninsurable.' },
  { id: 'freeze_damage', question: 'Was there pipe damage from a freeze event?', helpText: 'Texas freezes (like 2021) caused widespread pipe damage that may still need repair.' },
  { id: 'professional_opinion_plumbing', question: 'Has a plumber recommended repairs or repiping?', helpText: 'Include any inspection reports, leak detection results, or camera inspections.' },
];

const PlumbingChecklist: React.FC<PlumbingChecklistProps> = ({ 
  initialData, 
  onSave, 
  onBack,
  findingId,
  evidence = [],
  invoices = [],
  onEvidenceChange
}) => {
  const [answers, setAnswers] = useState<Record<string, boolean | null>>({});
  const [followUpData, setFollowUpData] = useState<FollowUpDataType>({});
  const [estimatedCost, setEstimatedCost] = useState<number>(0);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [initialized, setInitialized] = useState(false);
  
  // Helper blade state
  const [activeHelper, setActiveHelper] = useState<string | null>(null);

  // Only initialize from server data once, not on every evidence refresh
  // This prevents photo uploads from resetting unsaved checklist answers
  useEffect(() => {
    if (initialData && !initialized) {
      setAnswers(initialData.checklistData || {});
      setFollowUpData(initialData.followUpData || {});
      setEstimatedCost(initialData.estimatedCost || 0);
      setNotes(initialData.notes || '');
      setInitialized(true);
    }
  }, [initialData, initialized]);

  // Group evidence by checklist item
  const groupedEvidence = useMemo(() => {
    const grouped: Record<string, EvidenceDocument[]> = { _general: [] };
    for (const doc of evidence) {
      const key = doc.checklistItemId || '_general';
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(doc);
    }
    return grouped;
  }, [evidence]);

  const generalPhotos = groupedEvidence._general || [];

  const handleEvidenceUpload = () => {
    onEvidenceChange?.();
  };

  const handlePhotoUpload = (_evidence: EvidenceDocument) => {
    onEvidenceChange?.();
  };

  const handlePhotoDelete = (_evidenceId: string) => {
    onEvidenceChange?.();
  };

  const handleAnswer = (id: string, value: boolean) => {
    setAnswers(prev => ({ ...prev, [id]: value }));
    if (!value && followUpData[id]) {
      setFollowUpData(prev => {
        const newData = { ...prev };
        delete newData[id];
        return newData;
      });
    }
  };

  const handleFollowUpChange = (questionId: string, fieldName: string, value: string | number) => {
    setFollowUpData(prev => ({
      ...prev,
      [questionId]: {
        ...(prev[questionId] || {}),
        [fieldName]: value
      }
    }));
  };

  const issueCount = Object.values(answers).filter(v => v === true).length;

  const handleSave = async () => {
    // Validate that at least one question has been answered
    const answeredCount = Object.values(answers).filter(v => v !== null && v !== undefined).length;
    if (answeredCount === 0) {
      toast.error('Please answer at least one plumbing inspection question before saving. Go through the checklist and mark Yes or No for each item.');
      return;
    }

    // Clean followUpData: strip empty string values so optional fields don't cause validation errors
    const cleanedFollowUpData: FollowUpDataType = {};
    for (const [questionId, fields] of Object.entries(followUpData)) {
      const cleanedFields: Record<string, string | number> = {};
      for (const [key, val] of Object.entries(fields)) {
        if (val !== '' && val !== undefined && val !== null) {
          cleanedFields[key] = val;
        }
      }
      if (Object.keys(cleanedFields).length > 0) {
        cleanedFollowUpData[questionId] = cleanedFields;
      }
    }

    setSaving(true);
    try {
      await onSave({
        category: 'PLUMBING',
        checklistData: answers,
        followUpData: cleanedFollowUpData,
        estimatedCost,
        estimatedCostRange: getCostRange(estimatedCost),
        severity: getSeverity(issueCount),
        notes
      });
    } finally {
      setSaving(false);
    }
  };

  const getCostRange = (cost: number): string => {
    if (cost === 0) return 'Not estimated';
    if (cost < 50000) return '$0 - $500';
    if (cost < 150000) return '$500 - $1,500';
    if (cost < 400000) return '$1,500 - $4,000';
    if (cost < 1000000) return '$4,000 - $10,000';
    return '$10,000+';
  };

  const getSeverity = (issues: number): string => {
    if (issues === 0) return 'low';
    if (issues <= 2) return 'low';
    if (issues <= 4) return 'medium';
    if (issues <= 6) return 'high';
    return 'critical';
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100">
      {/* Header */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-cyan-50 rounded-lg text-cyan-600">
            <Droplets className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Plumbing Inspection</h2>
            <p className="text-gray-600">Document plumbing system issues</p>
          </div>
        </div>
      </div>

      {/* Checklist */}
      <div className="p-6">
        <div className="space-y-1">
          {plumbingQuestions.map(q => (
            <ChecklistQuestion
              key={q.id}
              id={q.id}
              question={q.question}
              helpText={q.helpText}
              value={answers[q.id] ?? null}
              onChange={(value) => handleAnswer(q.id, value)}
              followUpData={followUpData[q.id] || {}}
              onFollowUpChange={(fieldName, value) => handleFollowUpChange(q.id, fieldName, value)}
              onHelpClick={(itemId) => setActiveHelper(itemId)}
              {...(findingId ? {
                findingId,
                itemPhotos: groupedEvidence[q.id] || [],
                onPhotoUpload: handlePhotoUpload,
                onPhotoDelete: handlePhotoDelete
              } : {})}
            />
          ))}
        </div>

        {/* Issues Summary */}
        {issueCount > 0 && (
          <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-amber-800 font-medium">
              {issueCount} issue{issueCount !== 1 ? 's' : ''} identified
            </p>
          </div>
        )}

        {/* Repair Cost Section - Unified Invoice/Estimate Component */}
        <RepairCostSection
          category="plumbing"
          findingId={findingId}
          invoices={invoices}
          estimatedCost={estimatedCost}
          onEstimatedCostChange={setEstimatedCost}
          onInvoiceChange={handleEvidenceUpload}
          hasIssues={issueCount > 0}
        />

        {/* Notes */}
        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Additional Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="Add any additional details, pipe type, water heater age, plumber quotes, etc."
          />
        </div>

        {/* Photo Evidence Section - Only show if finding exists */}
        {findingId && (
          <div className="mt-8 p-6 bg-gray-50 rounded-xl border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <Camera className="w-5 h-5 text-gray-600" />
              <h3 className="font-bold text-gray-900">Additional Photos</h3>
            </div>
            <p className="text-gray-600 mb-4 text-sm">
              Upload any additional photos that don't fit a specific issue above. 
              You can also add photos directly to each issue when you mark it.
            </p>
            <PhotoUploader
              findingId={findingId}
              onUploadComplete={handleEvidenceUpload}
            />
            
            {generalPhotos.length > 0 && (
              <div className="mt-4">
                <h4 className="font-medium text-gray-900 mb-3 text-sm">General Photos ({generalPhotos.length})</h4>
                <EvidenceGallery
                  evidence={generalPhotos}
                  onDelete={handleEvidenceUpload}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="p-6 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-slate-900 text-white py-3 px-6 rounded-lg font-semibold hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {saving ? (
            <>Saving...</>
          ) : (
            <>
              <Save className="w-5 h-5" />
              Save Plumbing Assessment
            </>
          )}
        </button>
      </div>

      {/* Helper Blade */}
      <HelperBlade
        isOpen={activeHelper !== null}
        onClose={() => setActiveHelper(null)}
        itemId={activeHelper || ''}
        category="PLUMBING"
      />
    </div>
  );
};

export default PlumbingChecklist;
